import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useAttendanceDashboard } from '@/hooks/useAttendanceDashboard';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Calendar,
  ChevronRight,
  ChevronLeft,
  Sun,
  Palmtree,
  Clock,
  CalendarCheck,
  Users,
  Sparkles
} from 'lucide-react';
import { getRequestTypeLabel, getRequestStatusLabel } from '@/lib/attendanceTypes';
import { motion, AnimatePresence } from 'framer-motion';

// Circular progress component for the hero visualization
const CircularProgress = ({ value, max, size = 200, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* Background circle */}
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-slate-100"
        />
        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#progressGradient)"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
        />
        <defs>
          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
      </svg>
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          className="text-4xl font-bold text-slate-800"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {max - value}
        </motion.span>
        <span className="text-sm text-slate-500 font-medium">days left</span>
      </div>
    </div>
  );
};

// Stat pill component
const StatPill = ({ icon: Icon, label, value, color = "slate", delay = 0 }) => {
  const colorClasses = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className={`flex items-center gap-3 px-4 py-3 rounded-2xl border ${colorClasses[color]} transition-all duration-300 hover:scale-[1.02] hover:shadow-md cursor-default`}
    >
      <div className="p-2 rounded-xl bg-white/80 shadow-sm">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs font-medium opacity-70">{label}</p>
        <p className="text-lg font-bold -mt-0.5">{value}</p>
      </div>
    </motion.div>
  );
};

// Request card component - refined editorial style
const RequestCard = ({ request, formatDate, onClick, index }) => {
  const statusConfig = {
    pending: {
      bg: "bg-white",
      accent: "bg-amber-500",
      badge: "bg-amber-50 text-amber-700 border-amber-200",
      icon: Clock
    },
    approved: {
      bg: "bg-white",
      accent: "bg-emerald-500",
      badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
      icon: CalendarCheck
    },
    rejected: {
      bg: "bg-white",
      accent: "bg-slate-400",
      badge: "bg-slate-100 text-slate-600 border-slate-200",
      icon: Calendar
    },
  };

  const config = statusConfig[request.status] || statusConfig.pending;
  const StatusIcon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={onClick}
      className={`group relative ${config.bg} rounded-xl border border-slate-200/80
        hover:border-slate-300 hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)]
        transition-all duration-300 cursor-pointer overflow-hidden`}
    >
      {/* Status accent bar */}
      <div className={`absolute left-0 top-3 bottom-3 w-1 ${config.accent} rounded-full`} />

      <div className="flex items-stretch">
        {/* Main content */}
        <div className="flex-1 py-4 pl-5 pr-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center gap-2">
              <StatusIcon className="h-4 w-4 text-slate-400" />
              <span className="font-semibold text-slate-800 text-[15px]">
                {getRequestTypeLabel(request.leave_type)}
              </span>
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${config.badge}`}>
              {getRequestStatusLabel(request.status)}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="font-medium tabular-nums">
              {formatDate(request.start_date)}
            </span>
            <span className="text-slate-300">→</span>
            <span className="font-medium tabular-nums">
              {formatDate(request.end_date)}
            </span>
            {request.total_days && (
              <span className="text-slate-400 text-xs">
                ({request.total_days} {request.total_days === 1 ? 'day' : 'days'})
              </span>
            )}
          </div>

          {request.user_note && (
            <p className="text-sm text-slate-500 mt-2.5 pl-3 border-l-2 border-slate-200 line-clamp-1">
              {request.user_note}
            </p>
          )}
        </div>

        {/* Arrow indicator */}
        <div className="flex items-center px-4 border-l border-slate-100 group-hover:border-slate-200 transition-colors">
          <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all duration-200" />
        </div>
      </div>
    </motion.div>
  );
};

// Upcoming leave card - clean, modern treatment
const UpcomingCard = ({ request, formatDate, index }) => {
  const daysUntil = Math.ceil((new Date(request.start_date) - new Date()) / (1000 * 60 * 60 * 24));
  const isImminent = daysUntil <= 7;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="group relative bg-white rounded-xl border border-slate-200/80 overflow-hidden
        hover:border-slate-300 hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)] transition-all duration-300"
    >
      {/* Top section with countdown */}
      <div className={`px-5 py-4 ${isImminent ? 'bg-amber-50' : 'bg-slate-50'} border-b border-slate-100`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isImminent ? 'bg-amber-100' : 'bg-white border border-slate-200'}`}>
              <Palmtree className={`h-5 w-5 ${isImminent ? 'text-amber-600' : 'text-slate-500'}`} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Starts in</p>
              <p className={`text-2xl font-bold tracking-tight ${isImminent ? 'text-amber-700' : 'text-slate-800'}`}>
                {daysUntil} <span className="text-sm font-medium text-slate-400">days</span>
              </p>
            </div>
          </div>
          {isImminent && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
              Soon
            </span>
          )}
        </div>
      </div>

      {/* Content section */}
      <div className="px-5 py-4">
        <h3 className="font-semibold text-slate-800 text-[15px] mb-3">
          {getRequestTypeLabel(request.leave_type)}
        </h3>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">From</span>
            <span className="font-medium text-slate-700 tabular-nums">{formatDate(request.start_date)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">To</span>
            <span className="font-medium text-slate-700 tabular-nums">{formatDate(request.end_date)}</span>
          </div>
          {request.total_days && (
            <div className="flex items-center justify-between text-sm pt-2 mt-2 border-t border-slate-100">
              <span className="text-slate-500">Duration</span>
              <span className="font-semibold text-slate-800">
                {request.total_days} {request.total_days === 1 ? 'day' : 'days'}
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, can } = useAuth();
  const canManageAttendance = can('attendance.manage');

  usePageTitle('Attendance Dashboard');

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('recent');

  const {
    loading,
    error,
    stats,
    recentRequests,
    upcomingLeave
  } = useAttendanceDashboard(userProfile?.id, selectedYear);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
      const year = currentYear - 5 + i;
      return { value: year.toString(), label: year.toString() };
    });
  }, []);

  const formatDate = useCallback((dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }, []);

  const handleViewRequest = useCallback((request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  }, []);

  // Loading state
  if (loading && !stats.entitled) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-indigo-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-indigo-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="rounded-2xl bg-white border border-slate-200 p-8 mb-8"
      >
        <div>
          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-3xl font-bold text-slate-800 tracking-tight"
              >
                Attendance
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-slate-500 mt-1"
              >
                Your leave balance for {selectedYear}
              </motion.p>
            </div>

            {/* Year Selector */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center gap-1 bg-white rounded-full p-1 shadow-sm border border-slate-200"
            >
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedYear(selectedYear - 1)}
                className="h-8 w-8 rounded-full hover:bg-slate-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Select
                value={selectedYear.toString()}
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger className="w-[90px] h-8 border-0 bg-transparent focus:ring-0 font-semibold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year.value} value={year.value}>
                      {year.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedYear(selectedYear + 1)}
                className="h-8 w-8 rounded-full hover:bg-slate-100"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </motion.div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            {/* Circular Progress - Hero Element */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center lg:justify-start"
            >
              <CircularProgress
                value={stats.taken}
                max={stats.entitled}
                size={180}
                strokeWidth={14}
              />
            </motion.div>

            {/* Stats Pills */}
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatPill
                icon={Sun}
                label="Entitlement"
                value={`${stats.entitled} days`}
                color="indigo"
                delay={0.3}
              />
              <StatPill
                icon={Palmtree}
                label="Taken"
                value={`${stats.taken} days`}
                color="amber"
                delay={0.4}
              />
              <StatPill
                icon={Sparkles}
                label="Remaining"
                value={`${stats.remaining} days`}
                color="emerald"
                delay={0.5}
              />
              <StatPill
                icon={Clock}
                label="Pending"
                value={stats.pending_requests}
                color="violet"
                delay={0.6}
              />
              <StatPill
                icon={Users}
                label="On Leave Today"
                value={stats.on_leave_today}
                color="rose"
                delay={0.7}
              />
              <StatPill
                icon={CalendarCheck}
                label="Usage"
                value={`${stats.entitled > 0 ? Math.round((stats.taken / stats.entitled) * 100) : 0}%`}
                color="slate"
                delay={0.8}
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tabs Section */}
      <div className="space-y-6">
        {/* Custom Tab Switcher */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex gap-2"
        >
          <button
            onClick={() => setActiveTab('recent')}
            className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
              activeTab === 'recent'
                ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Recent Requests
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${
              activeTab === 'upcoming'
                ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Upcoming Leave
            {upcomingLeave.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs">
                {upcomingLeave.length}
              </span>
            )}
          </button>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'recent' && (
            <motion.div
              key="recent"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {recentRequests.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
                  <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Calendar className="h-7 w-7 text-slate-400" />
                  </div>
                  <p className="text-slate-700 font-semibold text-[15px]">No leave requests yet</p>
                  <p className="text-slate-500 text-sm mt-1.5 max-w-xs mx-auto">
                    When you submit leave requests, they'll appear here for tracking
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentRequests.map((request, index) => (
                    <RequestCard
                      key={request.id}
                      request={request}
                      formatDate={formatDate}
                      onClick={() => handleViewRequest(request)}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'upcoming' && (
            <motion.div
              key="upcoming"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              {upcomingLeave.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-slate-200">
                  <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Palmtree className="h-7 w-7 text-slate-400" />
                  </div>
                  <p className="text-slate-700 font-semibold text-[15px]">No upcoming leave scheduled</p>
                  <p className="text-slate-500 text-sm mt-1.5 max-w-xs mx-auto">
                    Your approved upcoming time off will be displayed here
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {upcomingLeave.map((request, index) => (
                    <UpcomingCard
                      key={request.id}
                      request={request}
                      formatDate={formatDate}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Leave Request Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-slate-200">
          {selectedRequest && (
            <>
              {/* Header */}
              <div className={`px-6 py-5 ${
                selectedRequest.status === 'approved' ? 'bg-emerald-50' :
                selectedRequest.status === 'rejected' ? 'bg-slate-50' :
                'bg-amber-50'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-slate-800">
                    {getRequestTypeLabel(selectedRequest.leave_type)}
                  </h3>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${
                    selectedRequest.status === 'approved'
                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                      : selectedRequest.status === 'rejected'
                      ? 'bg-slate-100 text-slate-600 border-slate-200'
                      : 'bg-amber-100 text-amber-700 border-amber-200'
                  }`}>
                    {getRequestStatusLabel(selectedRequest.status)}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {selectedRequest.total_days} {selectedRequest.total_days === 1 ? 'day' : 'days'} of leave
                </p>
              </div>

              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                {/* Dates */}
                <div className="flex gap-4">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-500 mb-1">From</p>
                    <p className="text-sm font-semibold text-slate-800 tabular-nums">
                      {formatDate(selectedRequest.start_date)}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-500 mb-1">To</p>
                    <p className="text-sm font-semibold text-slate-800 tabular-nums">
                      {formatDate(selectedRequest.end_date)}
                    </p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex gap-4 pt-3 border-t border-slate-100">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-500 mb-1">Requested</p>
                    <p className="text-sm text-slate-700 tabular-nums">
                      {formatDate(selectedRequest.created_date)}
                    </p>
                  </div>
                  {selectedRequest.approved_date && (
                    <div className="flex-1">
                      <p className="text-xs font-medium text-slate-500 mb-1">
                        {selectedRequest.status === 'approved' ? 'Approved' : 'Rejected'}
                      </p>
                      <p className="text-sm text-slate-700 tabular-nums">
                        {formatDate(selectedRequest.approved_date)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {selectedRequest.user_note && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-2">Your note</p>
                    <p className="text-sm text-slate-700 pl-3 border-l-2 border-slate-200">
                      {selectedRequest.user_note}
                    </p>
                  </div>
                )}

                {/* Rejection Reason */}
                {selectedRequest.status === 'rejected' && selectedRequest.rejection_reason && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-xs font-medium text-slate-500 mb-1.5">Reason for rejection</p>
                    <p className="text-sm text-slate-700">{selectedRequest.rejection_reason}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setDetailDialogOpen(false)}
                  className="text-slate-600 hover:text-slate-800"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setDetailDialogOpen(false);
                    navigate('/attendance/history');
                  }}
                  className="bg-slate-800 hover:bg-slate-700 text-white"
                >
                  View All Leave
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
